import React, { useState, useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import Scroll from 'react-scroll';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTasks, faTachometer, faEye, faPlus, faTrashCan, faVideo, faPencil, faArrowLeft, faTrash, faUsers } from '@fortawesome/free-solid-svg-icons'
import Select from 'react-select'
import { useRecoilState } from 'recoil';

import { EQUIPMENT_OPTIONS, USER_ACTIVITY_FEELING_WITH_EMPTY_OPTIONS, BREATHWORK_TYPE_WITH_EMPTY_OPTIONS } from "../../../Constants/FieldOptions";
import { getSessionDetailAPI, deleteSessionAPI } from "../../../API/Session";
import { getSelectedOptions } from "../../../Helpers/selectHelper";
import { putSessionUpdateAPI } from "../../../API/Session";
import FormErrorBox from "../../Reusable/FormErrorBox";
import DataDisplayRowText from "../../Reusable/DataDisplayRowText";
import FormMultiSelectField from "../../Reusable/FormMultiSelectField";
import DataDisplayRowRadio from "../../Reusable/DataDisplayRowRadio";
import FormUnixDateField from "../../Reusable/FormUnixDateField";
import DataDisplayRowCheckbox from "../../Reusable/DataDisplayRowCheckbox";
import DataDisplayRowSelect from "../../Reusable/DataDisplayRowSelect";
import DataDisplayRowTextSessionType from "../../Reusable/Detail/SessionType";
import PageLoadingContent from "../../Reusable/PageLoadingContent";
import { topAlertMessageState, topAlertStatusState, userDetailState } from "../../../AppState";


function UserSessionDetail() {
    ////
    //// Global state.
    ////

    const [topAlertMessage, setTopAlertMessage] = useRecoilState(topAlertMessageState);
    const [topAlertStatus, setTopAlertStatus] = useRecoilState(topAlertStatusState);
    const [user, setUser] = useRecoilState(userDetailState);

    ////
    //// URL Parameters.
    ////

    const { uid, sid } = useParams()

    ////
    //// Component states.
    ////

    const [errors, setErrors] = useState({});
    const [session, setSession] = useState({});
    const [showDeletionConfirmationModal, setShowDeletionConfirmationModal] = useState(false);
    const [forceURL, setForceURL] = useState("");
    const [isFetching, setFetching] = useState(false);

    ////
    //// API.
    ////

    // --- Detail --- //

    function onSessionDetailSuccess(response){
        console.log("onSessionDetailSuccess: Starting...");
        setSession(response);
    }

    function onSessionDetailError(apiErr) {
        console.log("onSessionDetailError: Starting...");
        setErrors(apiErr);

        // The following code will cause the screen to scroll to the top of
        // the page. Please see ``react-scroll`` for more information:
        // https://github.com/fisshy/react-scroll
        var scroll = Scroll.animateScroll;
        scroll.scrollToTop();
    }

    function onSessionDetailDone() {
        console.log("onSessionDetailDone: Starting...");
        setFetching(false);
    }

    // --- Deletion --- //

    function onSessionDeleteSuccess(response){
        console.log("onSessionDeleteSuccess: Starting..."); // For debugging purposes only.

        // Update notification.
        setTopAlertStatus("success");
        setTopAlertMessage("User deleted");
        setTimeout(() => {
            console.log("onDeleteConfirmButtonClick: topAlertMessage, topAlertStatus:", topAlertMessage, topAlertStatus);
            setTopAlertMessage("");
        }, 2000);

        // Upone success redirect user back to the `/users` list page.
        setForceURL("/users");
    }

    function onSessionDeleteError(apiErr) {
        console.log("onSessionDeleteError: Starting..."); // For debugging purposes only.
        setErrors(apiErr);

        // Update notification.
        setTopAlertStatus("danger");
        setTopAlertMessage("Failed deleting");
        setTimeout(() => {
            console.log("onSessionDeleteError: topAlertMessage, topAlertStatus:", topAlertMessage, topAlertStatus);
            setTopAlertMessage("");
        }, 2000);

        // The following code will cause the screen to scroll to the top of
        // the page. Please see ``react-scroll`` for more information:
        // https://github.com/fisshy/react-scroll
        var scroll = Scroll.animateScroll;
        scroll.scrollToTop();
    }

    function onSessionDeleteDone() {
        console.log("onSessionDeleteDone: Starting...");
        setFetching(false);
    }

    ////
    //// Event handling.
    ////

    const onDeleteConfirmButtonClick = () => {
        console.log("onDeleteConfirmButtonClick"); // For debugging purposes only.

        deleteSessionAPI(
            sid,
            onSessionDeleteSuccess,
            onSessionDeleteError,
            onSessionDeleteDone
        );
        setShowDeletionConfirmationModal(false);
    }


    ////
    //// Misc.
    ////

    useEffect(() => {
        let mounted = true;

        if (mounted) {
            window.scrollTo(0, 0);  // Start the page at the top of the page.

            setFetching(true);
            getSessionDetailAPI(
                sid,
                onSessionDetailSuccess,
                onSessionDetailError,
                onSessionDetailDone
            );
        }

        return () => { mounted = false; }
    }, [sid]);

    ////
    //// Component rendering.
    ////

    return (
        <>
            <div class="container">
                <section class="section">
                    {/* Desktop Breadcrumbs */}
                    <nav class="breadcrumb is-hidden-touch" aria-label="breadcrumbs">
                        <ul>
                            <li><Link to="/dashboard"><FontAwesomeIcon className="fas" icon={faTachometer} />&nbsp;Dashboard</Link></li>
                            <li class=""><Link to="/users" aria-current="page"><FontAwesomeIcon className="fas" icon={faUsers} />&nbsp;Users</Link></li>
                            <li class=""><Link to={`/user/${uid}/sessions`} aria-current="page"><FontAwesomeIcon className="fas" icon={faEye} />&nbsp;Detail (Sessions)</Link></li>
                            <li class="is-active"><Link aria-current="page"><FontAwesomeIcon className="fas" icon={faTasks} />&nbsp;Session</Link></li>
                        </ul>
                    </nav>

                    {/* Mobile Breadcrumbs */}
                    <nav class="breadcrumb is-hidden-desktop" aria-label="breadcrumbs">
                        <ul>
                            <li class=""><Link to={`/user/${uid}/sessions`} aria-current="page"><FontAwesomeIcon className="fas" icon={faArrowLeft} />&nbsp;Back to Detail (Sessions)</Link></li>
                        </ul>
                    </nav>

                    {/* Modals */}
                    <nav class="">
                        <div class={`modal ${showDeletionConfirmationModal ? 'is-active' : ''}`}>
                            <div class="modal-background"></div>
                            <div class="modal-card">
                                <header class="modal-card-head">
                                    <p class="modal-card-title">Are you sure?</p>
                                    <button class="delete" aria-label="close" onClick={(e, ses) => setShowDeletionConfirmationModal(false)}></button>
                                </header>
                                <section class="modal-card-body">
                                    You are about to delete this user session and all the data inside of it. This action is cannot be undone. Are you sure you would like to continue?
                                </section>
                                <footer class="modal-card-foot">
                                    <button class="button is-success" onClick={onDeleteConfirmButtonClick}>Confirm</button>
                                    <button class="button" onClick={(e, ses) => setShowDeletionConfirmationModal(false)}>Cancel</button>
                                </footer>
                            </div>
                        </div>
                    </nav>

                    {/* Page */}
                    <nav class="box">
                        {/* Title + Options */}
                        <div class="columns">
                            <div class="column">
                                <p class="title is-4"><FontAwesomeIcon className="fas" icon={faTasks} />&nbsp;Session - {user.firstName} {user.lastName}</p>
                            </div>
                            <div class="column has-text-right">
                                <Link to={`/user/${uid}/session/${sid}/edit`}class="button is-small is-warning is-fullwidth-mobile" type="button">
                                    <FontAwesomeIcon className="mdi" icon={faPencil} />&nbsp;Edit Session
                                </Link>
                                &nbsp;
                                <button onClick={(e, ses) => setShowDeletionConfirmationModal(true)} class="button is-small is-danger is-fullwidth-mobile" type="button">
                                    <FontAwesomeIcon className="mdi" icon={faTrashCan} />&nbsp;Delete Session
                                </button>
                            </div>
                        </div>

                        <div class="tabs is-medium">
                            <ul>
                                <li>
                                    <Link class="is-active" to={`/user/${uid}/session/${sid}`}><strong>Detail</strong></Link>
                                </li>
                                {(() => {
                                    // Defensive code: Only run this if we have data.
                                    if (session !== undefined && session !== null && session !== "") {
                                        // Show based on session type.
                                        switch (session.sessionType) {
                                            case "base": return (
                                                <li>
                                                    <Link to={`/user/${uid}/session/${sid}/video-sections`}>Video Sections</Link>
                                                </li>
                                            );
                                            case "sleep": return (
                                                <li>
                                                    <Link to={`/user/${uid}/session/${sid}/sleep-questions`}>Sleep Questions</Link>
                                                </li>
                                            );
                                            case "breath": return (
                                                <li>
                                                    <Link to={`/user/${uid}/session/${sid}/breathwork-options`}>Breathing Options</Link>
                                                </li>
                                            );
                                            default: return null;
                                        };
                                    }
                                    // In case we don't have a session template.
                                    return null;
                                })()}
                            </ul>
                        </div>

                        {isFetching
                            ?
                            <PageLoadingContent displayMessage={"Please wait"} />
                            :
                            <>
                                <div class="columns">
                                    <div class="column">
                                        <p class="subtitle"><FontAwesomeIcon className="fas" icon={faEye} />&nbsp;Detail</p>
                                        <DataDisplayRowText
                                            label="ID"
                                            value={session.id}
                                        />

                                        <FormUnixDateField
                                            label="Created"
                                            name="created"
                                            type="number"
                                            placeholder="0 or 1"
                                            value={session.created}
                                            maxWidth="120px"
                                            disabled={true}
                                        />

                                        <DataDisplayRowText
                                            label="No"
                                            value={session.no}
                                        />

                                        <DataDisplayRowText
                                            label="Completed Time (Optional)"
                                            type="datetime"
                                            value={session.completedTime}
                                        />

                                        <DataDisplayRowText
                                            label="Duration (Optional)"
                                            value={session.duration}
                                        />

                                        <DataDisplayRowText
                                            label="Total Duration (Optional)"
                                            value={session.totalDuration}
                                        />

                                        <DataDisplayRowText
                                            label="Name"
                                            value={session.name}
                                        />

                                        <DataDisplayRowText
                                            label="Description (Optional)"
                                            value={session.description}
                                        />

                                        <DataDisplayRowText
                                            label="Brief (Optional)"
                                            value={session.brief}
                                        />

                                        <DataDisplayRowText
                                            label="Motivational Text (Optional)"
                                            value={session.motivationalText}
                                        />

                                        <FormMultiSelectField
                                            label="Equipment (Optional)"
                                            name="equipment"
                                            placeholder="Text input"
                                            options={EQUIPMENT_OPTIONS}
                                            selectedValues={session.equipment}
                                            helpText="Pick the equipment associated with this session template."
                                            maxWidth="320px"
                                            disabled={true}
                                        />

                                        <DataDisplayRowText
                                            label="Target Heart Rate (Optional)"
                                            value={session.targetHeartRate}
                                        />

                                        <DataDisplayRowText
                                            label="Target Heart Rate Ratio (Optional)"
                                            value={session.targetHeartRateRatio}
                                        />

                                        <DataDisplayRowTextSessionType
                                            label="Session Type"
                                            value={session.sessionType}
                                        />

                                        <DataDisplayRowCheckbox
                                            label="Is primary session?"
                                            checked={session.isPrimarySession}
                                        />

                                        <DataDisplayRowSelect
                                            label="Feeling"
                                            selectedValue={session.feeling}
                                            options={USER_ACTIVITY_FEELING_WITH_EMPTY_OPTIONS}
                                        />

                                        <DataDisplayRowSelect
                                            label="Breathwork Type (Optional)"
                                            selectedValue={session.breathworkType}
                                            options={BREATHWORK_TYPE_WITH_EMPTY_OPTIONS}
                                        />

                                        <div class="columns">
                                            <div class="column is-half">
                                                <Link to={`/user/${uid}/sessions`} class="button is-fullwidth-mobile"><FontAwesomeIcon className="fas" icon={faArrowLeft} />&nbsp;Back to User Sessions</Link>
                                            </div>
                                            <div class="column is-half has-text-right">
                                                <Link to={`/user/${uid}/session/${sid}/edit`} class="button is-warning is-fullwidth-mobile"><FontAwesomeIcon className="fas" icon={faPencil} />&nbsp;Edit</Link>
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            </>
                        }
                    </nav>
                </section>
            </div>
        </>
    );
}

export default UserSessionDetail;
